import { defineStore } from 'pinia';
import { ref } from 'vue';
import { map, take } from 'rxjs';

import { isTransportIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { useEditingIngredientStore } from '@apps/main/new-desktop/core/stores';

import { CategoryType, TransportType } from '@libs/trip-planner-interfaces';
import type {
  AirportStaticInfo,
  FerryAutoCompleteSuggestion,
  PortInfo,
} from '@libs/trip-planner-interfaces';

import type { PortSelectionUpdatePayload } from '../../models';

import {
  getArrivalSuggestedAirport,
  getDepartureSuggestedAirport,
  getFerryRoutesSuggestions,
} from './portSelectorStoreLogic';

export const usePortSelectorStore = defineStore('PortSelectorStore', () => {
  const recipeStore = useRecipeStore();
  const mapStore = useMapStore();
  const editingIngredientStore = useEditingIngredientStore();

  const ingredientId = ref<string | undefined>(undefined);
  const initalFromPort = ref<PortInfo>();
  const initalToPort = ref<PortInfo>();
  const transportType = ref<TransportType.Flight | TransportType.Ferry>(
    TransportType.Flight
  );
  const canAddReturnIngredient = ref(false);
  const allowMultipleVehicles = ref<boolean>(false);
  const ferryRoutesSuggestions = ref<FerryAutoCompleteSuggestion[]>([]);

  function setModalData(
    currentIngredientId: string | undefined,
    fromPort: PortInfo | undefined,
    toPort: PortInfo | undefined,
    currentTransportType: TransportType.Flight | TransportType.Ferry,
    multipleVehicles: boolean
  ): void {
    ingredientId.value = currentIngredientId;
    initalFromPort.value = fromPort;
    initalToPort.value = toPort;
    transportType.value = currentTransportType;
    allowMultipleVehicles.value = multipleVehicles;

    tryToGetSuggestedAirportForModal();
    tryToGetSuggestedFerryRoutes();
    checkIfCanAddReturnIngredient();
  }

  function setPortsForTransport(payload: PortSelectionUpdatePayload): void {
    const { ports, addReturnTransport } = payload;
    const fromPort = ports.from;
    const toPort = ports.to;
    const transportType = ports.transportType;

    const newIngredientId =
      recipeStore.settingTransportIngredient?.ingredientId;
    if (newIngredientId) {
      const ingredientIndex = recipeStore.getIngredientIndex(newIngredientId);

      if (transportType === TransportType.Flight) {
        if (fromPort && fromPort.code && fromPort.label) {
          editingIngredientStore.editingFlightIngredient?.setDepartureAirport({
            iata: fromPort.code,
            name: fromPort.name,
            displayingText: fromPort.label,
          });
        }

        if (toPort && toPort.code && toPort.label) {
          editingIngredientStore.editingFlightIngredient?.setArrivalAirport({
            iata: toPort.code,
            name: toPort.name,
            displayingText: toPort.label,
          });
        }
      }

      if (transportType === TransportType.Ferry) {
        if (
          fromPort &&
          toPort &&
          fromPort.code &&
          fromPort.label &&
          toPort.code &&
          toPort.label &&
          ports.defaultVehicle
        ) {
          editingIngredientStore.editingFerryIngredient?.setPorts(
            {
              iata: fromPort.code,
              displayingText: fromPort.label,
            },
            {
              iata: toPort.code,
              displayingText: toPort.label,
            }
          );

          editingIngredientStore.editingFerryIngredient?.setVehicles([
            ports.defaultVehicle,
          ]);
          editingIngredientStore.editingFerryIngredient?.setAllowMultipleVehicles(
            ports.allowMultipleVehicles
          );
          editingIngredientStore.editingFerryIngredient?.setAllowVehicles(
            ports.allowVehicles
          );
        }
      }

      if (editingIngredientStore.editingTransportIngredient) {
        editingIngredientStore.editingTransportIngredient.isNewIngredient =
          false;
        if (addReturnTransport) {
          const mirroredTransport = recipeStore.getMirroredTransport(
            editingIngredientStore.editingTransportIngredient
          );
          recipeStore.addIngredient(mirroredTransport);
        }
        recipeStore.addIngredient(
          editingIngredientStore.editingTransportIngredient,
          ingredientIndex
        );
      }

      editingIngredientStore.doneEditingIngredient();
    }
  }

  function checkIfCanAddReturnIngredient(): void {
    const isSingleStayRecipe = recipeStore.stayOnlyRecipe.length === 1;
    const isSettingFirstTransport = recipeStore.isSettingFirstTransport;
    const lastIngredient = recipeStore.recipe[recipeStore.recipe.length - 1];
    const hasReturnTransport =
      lastIngredient && isTransportIngredient(lastIngredient);

    canAddReturnIngredient.value =
      isSingleStayRecipe && isSettingFirstTransport && !hasReturnTransport;
  }

  function tryToGetSuggestedAirportForModal(): void {
    if (
      initalFromPort.value &&
      initalToPort.value &&
      transportType.value === TransportType.Flight &&
      recipeStore.settingTransportIngredient
    ) {
      if (initalFromPort.value.code === undefined) {
        getArrivalSuggestedAirport(
          recipeStore.recipe,
          recipeStore.settingTransportIngredient,
          mapStore.endLocation
        )
          .pipe(take(1))
          .subscribe((airport: AirportStaticInfo | undefined) => {
            if (airport) {
              initalToPort.value = {
                code: airport.code,
                name: airport.city,
                label: airport.label,
              };
            }
          });
      }

      if (initalToPort.value.code === undefined) {
        getDepartureSuggestedAirport(
          recipeStore.recipe,
          recipeStore.settingTransportIngredient,
          mapStore.startingLocation
        )
          .pipe(take(1))
          .subscribe((airport: AirportStaticInfo | undefined) => {
            if (airport) {
              initalFromPort.value = {
                code: airport.code,
                name: airport.city,
                label: airport.label,
              };
            }
          });
      }
    }
  }

  function tryToGetSuggestedFerryRoutes(): void {
    if (
      transportType.value === TransportType.Ferry &&
      recipeStore.settingTransportIngredient
    ) {
      getFerryRoutesSuggestions(
        recipeStore.recipe,
        recipeStore.settingTransportIngredient,
        mapStore.startingLocation,
        mapStore.endLocation
      )
        .pipe(
          take(1),
          map((ferryRoutes) =>
            ferryRoutes.map(
              (ferryRoute): FerryAutoCompleteSuggestion => ({
                data: ferryRoute,
                category: '',
                categoryType: CategoryType.FerryRoute,
                label: `${ferryRoute.from.code}-${ferryRoute.to.code}`,
              })
            )
          )
        )
        .subscribe((routes) => {
          ferryRoutesSuggestions.value = routes;
        });
    }
  }

  function setTransportType(
    currentTransportType: TransportType.Flight | TransportType.Ferry
  ): void {
    transportType.value = currentTransportType;
  }

  return {
    allowMultipleVehicles,
    ingredientId,
    initalFromPort,
    initalToPort,
    transportType,
    canAddReturnIngredient,
    ferryRoutesSuggestions,
    setModalData,
    setPortsForTransport,
    tryToGetSuggestedFerryRoutes,
    setTransportType,
  };
});
